import React, { FC, useCallback, useEffect, useMemo, useRef } from "react";
import { shallowEqual } from "react-redux";

import { isMobile } from "react-device-detect";
import { SkeletonTheme } from "react-loading-skeleton";
import Drawer from "react-modern-drawer";
import "react-modern-drawer/dist/index.css";
import MidBox from "../../../../components/Boxes/MidBox";
import useWindowSize from "../../../../hooks/useWindowSize";
import { chatsSlice, ChatsState } from "../../store/chatSlice";

import { motion } from "framer-motion";
import { useChatsUIContext } from "../../context/ChatsContext";
import { getUserId } from "../../functions";
import { useChatDispatch, useChatSelector } from "../../hooks/reduxHook";
import { useChatNavigation } from "../../hooks/useChatNavigation";
import { ChatListType } from "../../types";
import { ChatEmitsEnum, ConversationTypeEnum } from "../../types/enums";
import ActionIconButton from "../Buttons/ActionIconButton";
import ChatHeader from "../chatHeader/ChatHeader";
import { useAppTranslation } from "../../../../i18n/useAppTranslation";

const { actions } = chatsSlice;

const ChatLayout: FC<{ children: React.ReactElement }> = ({ children }) => {
  const { t } = useAppTranslation("common");
  const windowSize = useWindowSize();
  const dispatch = useChatDispatch();
  const { backToPrevRoute } = useChatNavigation();

  const intervalRef = useRef<number | null>(null);

  const {
    conversationListByType,
    isOpen,
    socketIo,
    totalConversationList,
  }: ChatsState = useChatSelector((state: any) => {
    return {
      conversationListByType: state.chats.conversationListByType,
      isOpen: state.chats.isOpen,
      socketIo: state.chats.socketIo,
      totalConversationList: state.chats.totalConversationList,
    };
  }, shallowEqual) as ChatsState;

  const chatsContext = useChatsUIContext();
  const chatsUIProps = useMemo(() => {
    return {
      navigatePage: chatsContext?.navigatePage,
      setReplyingTo: chatsContext?.setReplyingTo,
      setNavigatePage: chatsContext?.setNavigatePage,
    };
  }, [
    chatsContext?.navigatePage,
    chatsContext?.setReplyingTo,
    chatsContext?.setNavigatePage,
  ]);

  const toggleDrawer = () => {
    dispatch(actions.setChatState(!isOpen));
    chatsContext?.resetContext();
    dispatch(actions.toggleDrawer());
  };

  const emitFunc = useCallback(() => {
    if (
      conversationListByType?.[ConversationTypeEnum.PERSONAL]?.length > 0 &&
      socketIo &&
      Object.keys(socketIo)?.length > 0
    ) {
      const personalConversations: number[] = [];
      conversationListByType[ConversationTypeEnum.PERSONAL].map(
        (conversation: ChatListType) => {
          if (conversation.type === ConversationTypeEnum.PERSONAL) {
            personalConversations.push(getUserId(conversation));
          }
        }
      );
      socketIo?.emit(ChatEmitsEnum.CHECK_ONLINE_USERS, personalConversations);
    }
  }, [conversationListByType, socketIo]);

  useEffect(() => {
    if (intervalRef.current === null && totalConversationList > 0 && socketIo) {
      const intervalId = window.setInterval(emitFunc, 15000);

      intervalRef.current = intervalId;

      return () => {
        clearInterval(intervalId);
        intervalRef.current = null;
      };
    }
  }, [totalConversationList, socketIo, emitFunc]);

  return (
    <SkeletonTheme baseColor="#3e382b" highlightColor="#474132">
      <Drawer
        open={isOpen}
        onClose={toggleDrawer}
        direction="left"
        zIndex={1089}
        size={
          isMobile
            ? windowSize.width < 420
              ? windowSize.width - 10
              : 400
            : 800
        }
        className="bg-transparent chat-box"
      >
        <motion.div style={{ height: "100%" }}>
          <MidBox
            className="transition overflow-hidden"
            title={t("chat.chats")}
            containerClass="px-sm-4 px-lg-4"
          >
            <ActionIconButton toggleClick={toggleDrawer} type="close" />
            {chatsUIProps?.navigatePage?.history &&
              chatsUIProps?.navigatePage?.history?.length > 0 && (
                <ActionIconButton
                  toggleClick={() => backToPrevRoute()}
                  type="back"
                />
              )}
            <div className="position-relative">
              <ChatHeader />
              {children}
            </div>
          </MidBox>
        </motion.div>
      </Drawer>
    </SkeletonTheme>
  );
};

export default ChatLayout;
